/*
 * RetechVN Pro – product page components.
 *
 * Custom elements: product-form, product-info, variant-selects, variant-radios,
 * secondary-variant-select, secondary-variant-select-separate, card-variant-selects,
 * sticky-atc, quantity-breaks, quantity-gifts, bundle-deals, main-bundle-offer,
 * product-info-upsell, custom-product-field.
 *
 * Depends on core.js (pub/sub, fetchConfig, formatMoney, routes, strings).
 */

(() => {
  /* ======================================================================
   * Shared helpers
   * ==================================================================== */

  const VARIANT_CHANGE_EVENT = 'product:variant-change';
  const UPSELL_CHANGE_EVENT = 'upsell:change';

  // Events already processed by a product-form (the layout's inline script and our own
  // listeners may both receive the same click on the sticky / secondary buttons).
  const handledEvents = new WeakSet();
  const jsonCache = new WeakMap();
  const currentVariants = new Map();
  const renderTokens = new Map();
  const stickyLabelCache = new WeakMap();

  const strings = () => window.variantStrings || {};

  function readVariants(container) {
    if (!container) return [];
    const script =
      container.querySelector(':scope > script[type="application/json"]') ||
      Array.from(container.querySelectorAll('script[type="application/json"]')).pop();
    if (!script) return [];
    if (!jsonCache.has(script)) {
      try {
        jsonCache.set(script, JSON.parse(script.textContent));
      } catch (error) {
        console.error('Invalid variant JSON', error);
        jsonCache.set(script, []);
      }
    }
    return jsonCache.get(script);
  }

  function moneyFormatFor(element) {
    const format = element && element.dataset ? element.dataset.moneyFormat : '';
    return format && format.includes('{{') ? format : window.moneyFormat;
  }

  function money(cents, format) {
    return formatMoney(Math.round(cents), format || window.moneyFormat);
  }

  // Mirrors Liquid's `money_without_trailing_zeros`.
  function moneyWithoutTrailingZeros(cents, format) {
    return money(cents, format).replace(/[.,]00(?!\d)/, '');
  }

  function toNumber(value, fallback) {
    const number = parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function resizeImageUrl(src, width) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', width);
      return url.href;
    } catch (error) {
      return src;
    }
  }

  function setImage(img, featuredImage, width) {
    if (!img || !featuredImage || !featuredImage.src) return;
    img.removeAttribute('srcset');
    img.src = resizeImageUrl(featuredImage.src, width);
    if (featuredImage.alt) img.alt = featuredImage.alt;
  }

  function byId(id) {
    return document.getElementById(id);
  }

  function allById(id) {
    return Array.from(document.querySelectorAll(`[id="${CSS.escape(id)}"]`));
  }

  function setButtonDisabled(button, disabled) {
    if (!button) return;
    if (disabled) button.setAttribute('disabled', 'disabled');
    else button.removeAttribute('disabled');
  }

  function setButtonLoading(button, loading) {
    if (!button) return;
    button.classList.toggle('loading', loading);
    if (loading) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function getTriggerButton(event) {
    if (!event) return null;
    if (event.submitter) return event.submitter;
    const target = event.currentTarget;
    if (target && target instanceof HTMLElement && target.matches('button, [type="submit"]')) return target;
    return null;
  }

  /** Finds the product-form a button belongs to (inside it, via form="", or by section id). */
  function resolveOwnerForm(button) {
    if (!button) return null;
    const inside = button.closest('product-form');
    if (inside) return inside;
    if (button.form) {
      const viaForm = button.form.closest('product-form');
      if (viaForm) return viaForm;
    }
    const match = (button.id || '').match(/^(?:SectionAtcBtn|ProductSubmitButton)-(.+)$/);
    if (match) {
      return byId(`ProductForm-${match[1]}`) || document.querySelector(`product-form[data-section="${CSS.escape(match[1])}"]`);
    }
    return null;
  }

  function getProductForm(sectionId) {
    return byId(`product-form-${sectionId}`);
  }

  function getProductInfo(sectionId) {
    return byId(`ProductInfo-${sectionId}`);
  }

  /** Quantity currently selected for a product section (quantity input or quantity break). */
  function getSectionQuantity(sectionId) {
    const form = getProductForm(sectionId);
    if (!form) return 1;
    const value = parseInt(new FormData(form).get('quantity'), 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /* ======================================================================
   * Option pickers (radios / selects) and variant resolution
   * ==================================================================== */

  class OptionPicker {
    /** @param {Array<{select?: HTMLSelectElement, radios?: HTMLInputElement[]}>} groups */
    constructor(groups) {
      this.groups = groups.filter((group) => group.select || (group.radios && group.radios.length));
    }

    static fromContainers(containers) {
      return new OptionPicker(
        Array.from(containers).map((container) => {
          const select = container.querySelector('select');
          if (select) return { select };
          return { radios: Array.from(container.querySelectorAll('input[type="radio"]')) };
        })
      );
    }

    static fromSelects(selects) {
      return new OptionPicker(Array.from(selects).map((select) => ({ select })));
    }

    get length() {
      return this.groups.length;
    }

    indexOf(element) {
      return this.groups.findIndex((group) =>
        group.select ? group.select === element : group.radios.includes(element)
      );
    }

    getValue(index) {
      const group = this.groups[index];
      if (!group) return undefined;
      if (group.select) return group.select.value;
      const checked = group.radios.find((radio) => radio.checked);
      return checked ? checked.value : undefined;
    }

    getValues() {
      return this.groups.map((_, index) => this.getValue(index));
    }

    getChoices(index) {
      const group = this.groups[index];
      if (!group) return [];
      if (group.select) return Array.from(group.select.options).map((option) => option.value);
      return group.radios.map((radio) => radio.value);
    }

    setValue(index, value) {
      const group = this.groups[index];
      if (!group) return;
      if (group.select) {
        group.select.value = value;
      } else {
        group.radios.forEach((radio) => {
          radio.checked = radio.value === value;
        });
      }
    }

    setValues(values) {
      values.forEach((value, index) => this.setValue(index, value));
    }

    /** @param {(value: string) => {exists: boolean, available: boolean}} getState */
    markAvailability(index, getState) {
      const group = this.groups[index];
      if (!group) return;
      const elements = group.select ? Array.from(group.select.options) : group.radios;
      const unavailableText = strings().unavailable_with_option;

      elements.forEach((element) => {
        const { exists, available } = getState(element.value);
        const targets = [element];
        if (element.parentElement && element.parentElement.classList.contains('color-swatch')) {
          targets.push(element.parentElement);
        }
        targets.forEach((target) => {
          target.classList.toggle('non-existent', !exists);
          target.classList.toggle('unavailable', exists && !available);
        });

        if (group.select) {
          element.textContent =
            available || !unavailableText ? element.value : unavailableText.replace('[value]', element.value);
        } else {
          element.classList.toggle('disabled', !available);
        }
      });
    }
  }

  function matchesPrefix(variant, values, length) {
    for (let i = 0; i < length; i += 1) {
      if (variant.options[i] !== values[i]) return false;
    }
    return true;
  }

  function findVariant(variants, values) {
    return variants.find((variant) => variant.options.every((option, index) => option === values[index])) || null;
  }

  /**
   * Reads the picker, optionally moves later options away from non-existent / sold out
   * combinations, refreshes availability classes and returns the matching variant.
   */
  function resolveSelection(picker, variants, { skipNonExistent = false, skipUnavailable = false } = {}) {
    const values = picker.getValues();

    for (let index = 1; index < picker.length; index += 1) {
      const candidates = variants.filter((variant) => matchesPrefix(variant, values, index));
      const isAcceptable = (value) =>
        candidates.some((variant) => variant.options[index] === value && (!skipUnavailable || variant.available));
      if (isAcceptable(values[index])) continue;

      const exists = candidates.some((variant) => variant.options[index] === values[index]);
      if ((exists && !skipUnavailable) || (!exists && !skipNonExistent && !skipUnavailable)) continue;

      const choices = picker.getChoices(index);
      const replacement =
        choices.find(isAcceptable) ||
        choices.find((value) => candidates.some((variant) => variant.options[index] === value));
      if (replacement !== undefined) {
        picker.setValue(index, replacement);
        values[index] = replacement;
      }
    }

    for (let index = 0; index < picker.length; index += 1) {
      const candidates = variants.filter((variant) => matchesPrefix(variant, values, index));
      picker.markAvailability(index, (value) => {
        const matching = candidates.filter((variant) => variant.options[index] === value);
        return { exists: matching.length > 0, available: matching.some((variant) => variant.available) };
      });
    }

    return findVariant(variants, values);
  }

  /** Best effort match: exact, else first variant sharing the changed option value. */
  function findClosestVariant(variants, values, changedIndex) {
    const exact = findVariant(variants, values);
    if (exact) return exact;
    const sameValue = variants.filter((variant) => variant.options[changedIndex] === values[changedIndex]);
    return sameValue.find((variant) => variant.available) || sameValue[0] || null;
  }

  function findMainPicker(sectionId) {
    const id = CSS.escape(sectionId);
    return document.querySelector(`variant-selects[data-section="${id}"], variant-radios[data-section="${id}"]`);
  }

  /* ======================================================================
   * Custom fields & ATC button state
   * ==================================================================== */

  function getCustomFields(sectionId) {
    return Array.from(document.querySelectorAll(`custom-product-field[data-section="${CSS.escape(sectionId)}"]`));
  }

  function getCustomFieldProperties(sectionId) {
    const properties = {};
    getCustomFields(sectionId).forEach((field) => {
      if (typeof field.getValue !== 'function') return;
      const value = field.getValue();
      if (field.dataset.name && value !== '') properties[field.dataset.name] = value;
    });
    return properties;
  }

  /** Shows errors on invalid required fields; returns true when everything is filled in. */
  function validateRequiredFields(sectionId) {
    const invalid = getCustomFields(sectionId).filter((field) => field.isValid && !field.isValid());
    invalid.forEach((field) => field.showError(true));
    if (invalid.length) {
      const control = invalid[0].querySelector('input, textarea, select');
      if (control) control.focus();
    }
    return invalid.length === 0;
  }

  /** "Add to cart" buttons other sections render for the page product (theme.liquid binds them to the main form). */
  function getPageProductButtons(sectionId) {
    const form = byId(`ProductForm-${sectionId}`);
    if (!form || form.dataset.main !== 'true' || form.closest('quick-add-modal')) return [];
    const ownId = `SectionAtcBtn-${sectionId}`;
    return Array.from(document.querySelectorAll('[id^="SectionAtcBtn-"].main-product-atc')).filter(
      (button) => button.id !== ownId && !button.closest('quick-add-modal')
    );
  }

  function getAtcButtons(sectionId) {
    return {
      main: byId(`ProductSubmitButton-${sectionId}`),
      secondary: allById(`SectionAtcBtn-${sectionId}`),
      pageProduct: getPageProductButtons(sectionId),
    };
  }

  function isMarkedUnavailable(button) {
    if (button.dataset.unavailable === undefined) {
      button.dataset.unavailable = String(button.hasAttribute('disabled'));
    }
    return button.dataset.unavailable === 'true';
  }

  /** Applies "sold out" and "required custom field" state to every ATC button of a section. */
  function refreshAtcState(sectionId) {
    const { main, secondary, pageProduct } = getAtcButtons(sectionId);
    if (!main && !secondary.length && !pageProduct.length) return;

    const required = getCustomFields(sectionId).filter((field) => field.dataset.required === 'true');
    const invalid = required.filter((field) => field.isValid && !field.isValid());
    const lastInvalid = invalid[invalid.length - 1];
    const stickyInvalid = invalid.filter((field) => field.dataset.applyStickyAtcError === 'true').pop();

    if (main) {
      const unavailable = isMarkedUnavailable(main);
      main.dataset.requiredFields = String(required.length);
      main.dataset.validFields = String(required.length - invalid.length);
      setButtonDisabled(main, unavailable || invalid.length > 0);
      toggleButtonError(main, '.main-atc__error', '.main-atc__label', !unavailable && lastInvalid, lastInvalid);
    }

    secondary.forEach((button) => {
      const unavailable = isMarkedUnavailable(button);
      setButtonDisabled(button, unavailable || invalid.length > 0);
      toggleButtonError(button, '.sticky-atc__error', '.sticky-atc__label', !unavailable && stickyInvalid, stickyInvalid);
    });

    // Sold-out state only: they have no error label, so they stay clickable and handleSubmit flags the empty field.
    pageProduct.forEach((button) => setButtonDisabled(button, isMarkedUnavailable(button)));
  }

  function toggleButtonError(button, errorSelector, labelSelector, show, field) {
    const error = button.querySelector(errorSelector);
    const label = button.querySelector(labelSelector);
    if (!error) return;
    error.textContent = show && field ? field.dataset.atcErrorMsg || '' : '';
    error.style.display = show ? '' : 'none';
    if (label) label.style.display = show ? 'none' : '';
  }

  /** Immediate (client side) button update for a newly selected variant. */
  function setVariantAvailability(sectionId, variant) {
    const available = Boolean(variant && variant.available);
    const { addToCart, soldOut, unavailable } = strings();
    const text = !variant ? unavailable : available ? addToCart : soldOut;
    const { main, secondary, pageProduct } = getAtcButtons(sectionId);
    pageProduct.forEach((button) => {
      button.dataset.unavailable = String(!available);
    });

    if (main) {
      main.dataset.unavailable = String(!available);
      const label = main.querySelector('.main-atc__label__text');
      if (label && text) label.textContent = text;
      const price = byId(`main-atc-price-${sectionId}`);
      if (price) {
        price.classList.toggle('hidden', !available);
        if (variant) price.textContent = `${money(variant.price)} •`;
      }
    }

    secondary.forEach((button) => {
      button.dataset.unavailable = String(!available);
      const label = button.querySelector('.sticky-atc__label');
      if (!label) return;
      if (!stickyLabelCache.has(label) && label.querySelector('.sticky-atc__label__text')) {
        stickyLabelCache.set(label, label.innerHTML);
      }
      if (!available) {
        if (text) label.textContent = text;
      } else if (stickyLabelCache.has(label)) {
        label.innerHTML = stickyLabelCache.get(label);
        const price = label.querySelector(`[id="sticky-atc-price-${CSS.escape(sectionId)}"]`);
        if (price) price.textContent = `${money(variant.price)} •`;
      }
    });

    refreshAtcState(sectionId);
  }

  /* ======================================================================
   * Variant change flow for a product section
   * ==================================================================== */

  /**
   * @typedef {Object} SectionContext
   * @property {string} sectionId           Id used in the current DOM (may be "quickadd-…").
   * @property {string} originalSectionId   Id used for the Section Rendering API.
   * @property {string} url                 Product URL.
   * @property {boolean} updateUrl          Whether to update ?variant= in the address bar.
   */

  function buildContext(element) {
    const sectionId = element.dataset.section;
    const productInfo = getProductInfo(sectionId);
    return {
      sectionId,
      originalSectionId:
        element.dataset.originalSection || (productInfo && productInfo.dataset.originalSection) || sectionId,
      url:
        element.dataset.url ||
        (productInfo && productInfo.dataset.url) ||
        `${window.location.origin}${window.location.pathname}`,
      updateUrl: element.dataset.updateUrl !== 'false',
    };
  }

  function applyVariantChange(context, variant, source = null) {
    const { sectionId } = context;
    currentVariants.set(sectionId, variant);

    updateVariantInputs(sectionId, variant);
    setVariantAvailability(sectionId, variant);

    document.dispatchEvent(
      new CustomEvent(VARIANT_CHANGE_EVENT, { detail: { sectionId, variant, source } })
    );

    if (!variant) {
      setPriceUnavailable(sectionId);
      return;
    }

    updateMedia(sectionId, variant);
    updateStickyImage(sectionId, variant);
    if (context.updateUrl) {
      window.history.replaceState({}, '', `${context.url}?variant=${variant.id}`);
    }
    updateShareUrl(sectionId, context.url, variant.id);
    updatePickupAvailability(sectionId, variant);
    renderSection(context, variant);
  }

  function updateVariantInputs(sectionId, variant) {
    if (!variant) return;
    [`product-form-${sectionId}`, `product-form-installment-${sectionId}`].forEach((formId) => {
      const form = byId(formId);
      const input = form && form.querySelector('input[name="id"]');
      if (!input) return;
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function setPriceUnavailable(sectionId) {
    [`price-${sectionId}`, `Sku-${sectionId}`, `Inventory-${sectionId}`].forEach((id) => {
      const element = byId(id);
      if (element) element.classList.add('visibility-hidden');
    });
  }

  function updateMedia(sectionId, variant) {
    const mediaId = variant.featured_media && variant.featured_media.id;
    const galleries = document.querySelectorAll(`[id^="MediaGallery-${CSS.escape(sectionId)}"]`);

    galleries.forEach((gallery) => {
      if (typeof gallery.setActiveMedia !== 'function') return;
      const filteringOption = gallery.dataset.filteringOption;
      const filtering = Boolean(filteringOption && variant[filteringOption] !== undefined);
      let target = mediaId ? `${sectionId}-${mediaId}` : null;

      if (target && !gallery.querySelector(`[data-media-id="${CSS.escape(target)}"]`)) target = null;
      if (filtering) {
        // Prefer a slide whose alt text matches the selected option (media-gallery hides the rest).
        const normalize = (value) => String(value ?? '').trim().toLowerCase();
        const selected = normalize(variant[filteringOption]);
        const slides = Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]'));
        const targetSlide = target && slides.find((item) => item.dataset.mediaId === target);
        const targetMatches = targetSlide && normalize(targetSlide.dataset.alt) === selected;
        if (!targetMatches) {
          const match = slides.find((item) => normalize(item.dataset.alt) === selected);
          if (match) target = match.dataset.mediaId;
        }
        if (!target && slides[0]) target = slides[0].dataset.mediaId;
      }
      if (!target) return;
      gallery.setActiveMedia(target, !filtering, filtering, variant);
    });

    const modalContent = document.querySelector(`#ProductModal-${CSS.escape(sectionId)} .product-media-modal__content`);
    if (modalContent && mediaId) {
      const modalMedia = modalContent.querySelector(`:scope > [data-media-id="${mediaId}"]`);
      if (modalMedia) modalContent.prepend(modalMedia);
    }
  }

  function updateStickyImage(sectionId, variant) {
    setImage(byId(`sticky-atc-image-${sectionId}`), variant.featured_image, 500);
  }

  function updateShareUrl(sectionId, url, variantId) {
    const shareButton = byId(`Share-${sectionId}`);
    if (!shareButton || !url) return;
    const origin = window.shopUrl || window.location.origin;
    const shareUrl = variantId ? `${origin}${url}?variant=${variantId}` : `${origin}${url}`;
    if (typeof shareButton.updateUrl === 'function') {
      shareButton.updateUrl(shareUrl);
    } else {
      const input = shareButton.querySelector('input');
      if (input) input.value = shareUrl;
    }
  }

  function updatePickupAvailability(sectionId, variant) {
    const productInfo = getProductInfo(sectionId);
    const pickup = productInfo && productInfo.querySelector('pickup-availability');
    if (!pickup) return;
    if (variant.available && typeof pickup.fetchAvailability === 'function' && pickup.errorHtml) {
      pickup.setAttribute('available', '');
      pickup.fetchAvailability(variant.id);
    } else {
      pickup.removeAttribute('available');
      pickup.innerHTML = '';
    }
  }

  /** Fetches the section for the variant (Section Rendering API) and copies the variant specific parts. */
  async function renderSection(context, variant) {
    const { sectionId, originalSectionId, url } = context;
    const token = {};
    renderTokens.set(sectionId, token);

    try {
      const response = await fetch(`${url}?variant=${variant.id}&section_id=${encodeURIComponent(originalSectionId)}`);
      let text = await response.text();
      if (renderTokens.get(sectionId) !== token) return;
      if (originalSectionId !== sectionId) text = text.split(originalSectionId).join(sectionId);

      const html = new DOMParser().parseFromString(text, 'text/html');
      updateFromRenderedSection(html, sectionId);
      refreshAtcState(sectionId);
      publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html, variant } });
    } catch (error) {
      console.error(error);
    }
  }

  function copyInner(html, id, { syncHiddenClass = false } = {}) {
    const destination = byId(id);
    const source = html.getElementById(id);
    if (!destination || !source) return null;
    destination.innerHTML = source.innerHTML;
    if (syncHiddenClass) {
      ['visibility-hidden', 'hidden'].forEach((name) =>
        destination.classList.toggle(name, source.classList.contains(name))
      );
    }
    return destination;
  }

  function updateFromRenderedSection(html, sectionId) {
    const price = copyInner(html, `price-${sectionId}`);
    if (price) price.classList.remove('visibility-hidden');
    copyInner(html, `Sku-${sectionId}`, { syncHiddenClass: true });
    copyInner(html, `Inventory-${sectionId}`, { syncHiddenClass: true });
    copyInner(html, `sticky-atc-separate-price-${sectionId}`);

    // Button labels (text + optional price) as rendered by Liquid for this variant.
    [
      [`ProductSubmitButton-${sectionId}`, '.main-atc__label'],
      [`SectionAtcBtn-${sectionId}`, '.sticky-atc__label'],
    ].forEach(([id, selector]) => {
      const source = html.querySelector(`[id="${CSS.escape(id)}"] ${selector}`);
      if (!source) return;
      allById(id).forEach((button) => {
        const label = button.querySelector(selector);
        if (!label) return;
        label.innerHTML = source.innerHTML;
        if (selector === '.sticky-atc__label' && label.querySelector('.sticky-atc__label__text')) {
          stickyLabelCache.set(label, label.innerHTML);
        }
      });
    });

    // Custom option labels may include the [selected] value.
    document.querySelectorAll(`[id^="custom-label-${CSS.escape(sectionId)}-"]`).forEach((label) => {
      const source = html.getElementById(label.id);
      if (source) label.innerHTML = source.innerHTML;
    });

    updateQuantityRules(html, sectionId);

    // Quantity-breaks style option pickers show per-value prices that depend on other options.
    const picker = byId(`variant-selects-${sectionId}`);
    const sourcePicker = html.getElementById(`variant-selects-${sectionId}`);
    if (picker && sourcePicker) {
      const sources = sourcePicker.querySelectorAll('.product-form__input--quantity-breaks .quantity-breaks-container');
      picker
        .querySelectorAll('.product-form__input--quantity-breaks .quantity-breaks-container')
        .forEach((container, index) => {
          if (sources[index]) container.innerHTML = sources[index].innerHTML;
        });
    }
  }

  function updateQuantityRules(html, sectionId) {
    const container = byId(`Quantity-Form-${sectionId}`);
    const source = html.getElementById(`Quantity-Form-${sectionId}`);
    if (!container || !source) return;

    ['.quantity__rules', '.quantity__rules-cart'].forEach((selector) => {
      const destination = container.querySelector(selector);
      const origin = source.querySelector(selector);
      if (!destination || !origin) return;
      destination.innerHTML = origin.innerHTML;
      destination.classList.toggle('hidden', origin.classList.contains('hidden'));
    });

    const input = container.querySelector('.quantity__input');
    const sourceInput = source.querySelector('.quantity__input');
    if (!input || !sourceInput) return;
    ['data-cart-quantity', 'data-min', 'data-max', 'step'].forEach((attribute) => {
      const value = sourceInput.getAttribute(attribute);
      if (value === null) input.removeAttribute(attribute);
      else input.setAttribute(attribute, value);
    });
  }

  /* ======================================================================
   * Payload helpers
   * ==================================================================== */

  function collectUpsellItems(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll('product-info-upsell'))
      .filter(
        (upsell) =>
          upsell.dataset.toggle !== 'false' &&
          upsell.dataset.selected === 'true' &&
          upsell.dataset.available !== 'false' &&
          upsell.dataset.id
      )
      .map((upsell) => ({ id: Number(upsell.dataset.id), quantity: 1 }));
  }

  function collectGiftItems(sectionId) {
    const gifts = document.querySelector(`quantity-gifts[data-section="${CSS.escape(sectionId)}"]`);
    if (!gifts || typeof gifts.getItems !== 'function') return [];
    return gifts.getItems();
  }

  /* ======================================================================
   * <product-form>
   * ==================================================================== */

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.onSubmit = this.handleSubmit.bind(this);
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.idInput = this.form.querySelector('[name="id"]');
      // Hidden id inputs are rendered disabled for no-JS fallbacks.
      if (this.idInput && this.idInput.value) this.idInput.disabled = false;

      this.form.addEventListener('submit', this.onSubmit);
      this.extraButtons = this.dataset.section ? allById(`SectionAtcBtn-${this.dataset.section}`) : [];
      this.extraButtons.forEach((button) => button.addEventListener('click', this.onSubmit));
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      (this.extraButtons || []).forEach((button) => button.removeEventListener('click', this.onSubmit));
    }

    get sectionId() {
      return this.dataset.section || '';
    }

    /** product-info element when this is the main form of a product section. */
    get productSection() {
      return this.sectionId ? getProductInfo(this.sectionId) : null;
    }

    get bundle() {
      const bundle =
        this.closest('bundle-deals') || (this.sectionId ? byId(`bundle-deals-${this.sectionId}`) : null);
      return bundle && typeof bundle.getItems === 'function' ? bundle : null;
    }

    get errorWrapper() {
      const own = this.querySelector('.product-form__error-message-wrapper');
      if (own) return own;
      const info = this.closest('product-info');
      return info ? info.querySelector('.product-form__error-message-wrapper') : null;
    }

    async handleSubmit(event) {
      if (event) {
        if (handledEvents.has(event)) return;
        const owner = resolveOwnerForm(getTriggerButton(event));
        if (owner && owner !== this && typeof owner.handleSubmit === 'function') {
          owner.handleSubmit(event);
          return;
        }
        handledEvents.add(event);
        event.preventDefault();
      }
      if (this.isSubmitting || !this.form) return;

      const trigger = getTriggerButton(event);
      this.handleErrorMessage();

      if (this.productSection && !validateRequiredFields(this.sectionId)) return;

      let payload;
      try {
        payload = this.buildPayload();
      } catch (error) {
        this.handleErrorMessage(error.message);
        return;
      }
      if (!payload.items.length) {
        this.handleErrorMessage(strings().unavailable || window.cartStrings?.error);
        return;
      }

      const skipCart =
        (trigger && trigger.dataset.skipCartButton === 'true') ||
        (this.idInput && this.idInput.dataset.skipCart === 'true');
      const cart = getCartElement();
      // Without a drawer / notification (cart type "page") cart.js still renders the cart bubble.
      const sectionRenderer = cart || (window.theme && theme.cartSections) || null;
      const body = { items: payload.items };
      if (sectionRenderer && typeof sectionRenderer.getSectionsToRender === 'function' && !skipCart) {
        body.sections = sectionRenderer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);

      const buttons = this.getLoadingButtons(trigger);
      this.isSubmitting = true;
      buttons.forEach((button) => setButtonLoading(button, true));
      let keepLoading = false;

      try {
        const response = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          ...fetchConfig('json'),
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: payload.mainId,
            errors: data.errors || data.description,
            message: data.message,
          });
          this.handleErrorMessage(data.description || data.message || window.cartStrings?.error);
          this.showSoldOutMessage();
          return;
        }

        const mainItem =
          (data.items || []).find((item) => String(item.variant_id) === String(payload.mainId)) ||
          (data.items || [])[0] ||
          {};
        const cartData = { ...mainItem, ...data };

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: payload.mainId, cartData });

        if (skipCart) {
          keepLoading = true;
          window.location.href = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}checkout`;
          return;
        }
        if (!cart) {
          if (sectionRenderer && typeof sectionRenderer.render === 'function' && data.sections) {
            sectionRenderer.render(cartData);
          }
          keepLoading = true;
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }

        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

        // Covers regular forms as well as hidden `data-is-cart-upsell` forms inside the drawer.
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal && typeof quickAddModal.hide === 'function') {
          quickAddModal.hide(true);
          window.setTimeout(() => cart.renderContents(cartData), 300);
        } else {
          cart.renderContents(cartData);
        }
      } catch (error) {
        console.error(error);
        this.handleErrorMessage(window.cartStrings?.error);
      } finally {
        this.isSubmitting = false;
        if (!keepLoading) buttons.forEach((button) => setButtonLoading(button, false));
      }
    }

    /** Builds the `items` array for /cart/add.js. */
    buildPayload() {
      const bundle = this.bundle;
      if (bundle) {
        const items = bundle.getItems();
        if (this.dataset.includePdpUpsells === 'true') items.push(...collectUpsellItems(this.closest('product-info')));
        return { items, mainId: items.length ? items[0].id : null };
      }

      const formData = new FormData(this.form);
      const mainId = Number(formData.get('id') || (this.idInput && this.idInput.value));
      if (!mainId) throw new Error(strings().unavailable || '');

      const quantity = parseInt(formData.get('quantity'), 10) || 1;
      const sellingPlan = formData.get('selling_plan');
      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
      });

      const section = this.productSection;
      if (section) Object.assign(properties, getCustomFieldProperties(this.sectionId));

      const lineItem = (id, qty) => {
        const item = { id: Number(id), quantity: qty };
        if (Object.keys(properties).length) item.properties = { ...properties };
        if (sellingPlan) item.selling_plan = sellingPlan;
        return item;
      };

      let items = [lineItem(mainId, quantity)];

      if (section) {
        const quantityBreaks = section.querySelector('quantity-breaks');
        const breakItems =
          quantityBreaks && typeof quantityBreaks.getSelectedVariantItems === 'function'
            ? quantityBreaks.getSelectedVariantItems()
            : null;
        if (breakItems && breakItems.length) items = breakItems.map((item) => lineItem(item.id, item.quantity));

        items.push(...collectUpsellItems(section));
        items.push(...collectGiftItems(this.sectionId));
      }

      return { items, mainId: items[0].id };
    }

    getLoadingButtons(trigger) {
      const buttons = new Set(this.form.querySelectorAll('[type="submit"]'));
      if (this.form.id) {
        document.querySelectorAll(`button[form="${CSS.escape(this.form.id)}"]`).forEach((b) => buttons.add(b));
      }
      if (this.sectionId) {
        allById(`SectionAtcBtn-${this.sectionId}`).forEach((b) => buttons.add(b));
        const quickAdd = this.closest('quick-add-modal');
        if (this.dataset.main === 'true') {
          (quickAdd || document).querySelectorAll('.main-product-atc').forEach((b) => {
            if (quickAdd || !b.closest('quick-add-modal')) buttons.add(b);
          });
        } else {
          document.querySelectorAll(`.featured-product-atc-${CSS.escape(this.sectionId)}`).forEach((b) => buttons.add(b));
        }
      }
      if (trigger) buttons.add(trigger);
      return Array.from(buttons).filter((button) => !button.classList.contains('hidden'));
    }

    handleErrorMessage(message = false) {
      const wrapper = this.errorWrapper;
      if (!wrapper) return;
      const text = wrapper.querySelector('.product-form__error-message');
      wrapper.toggleAttribute('hidden', !message);
      if (text) text.textContent = message || '';
    }

    // Card quick-add buttons have no error wrapper: show their "sold out" message instead.
    showSoldOutMessage() {
      const button = this.form.querySelector('[type="submit"][data-sold-out-message="true"]');
      if (!button) return;
      const message = button.querySelector('.sold-out-message');
      const label = button.querySelector('.main-atc__label__text, span');
      if (!message) return;
      message.classList.remove('hidden');
      if (label && label !== message) label.classList.add('hidden');
    }
  }

  /* ======================================================================
   * <product-info>
   * ==================================================================== */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.unsubscribers = [
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event && event.data && event.data.sectionId === this.sectionId) this.setQuantityBoundaries();
        }),
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.fetchQuantityRules()),
      ];
      this.initShareLinks();
    }

    disconnectedCallback() {
      (this.unsubscribers || []).forEach((unsubscribe) => unsubscribe());
    }

    get sectionId() {
      return this.dataset.section;
    }

    get originalSectionId() {
      return this.dataset.originalSection || this.sectionId;
    }

    get currentVariantId() {
      const form = getProductForm(this.sectionId);
      const input = form && form.querySelector('[name="id"]');
      return input ? input.value : '';
    }

    get quantityInput() {
      return byId(`Quantity-${this.sectionId}`) || this.querySelector('.quantity__input');
    }

    initShareLinks() {
      updateShareUrl(this.sectionId, this.dataset.url, this.currentVariantId);
    }

    /** Applies min / max / step rules, taking the quantity already in the cart into account. */
    setQuantityBoundaries() {
      const input = this.quantityInput;
      if (!input) return;

      const cartQuantity = parseInt(input.dataset.cartQuantity, 10) || 0;
      const min = parseInt(input.dataset.min, 10) || 1;
      const max = input.dataset.max ? parseInt(input.dataset.max, 10) : null;
      const step = parseInt(input.step, 10) || 1;

      let effectiveMin = min;
      const effectiveMax = max === null ? null : Math.max(max - cartQuantity, 0);
      if (effectiveMax !== null) effectiveMin = Math.min(effectiveMin, effectiveMax);
      if (cartQuantity >= min) effectiveMin = Math.min(effectiveMin, step);

      input.min = effectiveMin;
      if (effectiveMax === null) input.removeAttribute('max');
      else input.max = effectiveMax;
      input.value = effectiveMin;

      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
    }

    async fetchQuantityRules() {
      const variantId = this.currentVariantId;
      const container = byId(`Quantity-Form-${this.sectionId}`);
      if (!variantId || !container || !this.dataset.url) return;

      const spinner = container.querySelector('.quantity__rules-cart .loading-overlay');
      if (spinner) spinner.classList.remove('hidden');
      try {
        const response = await fetch(
          `${this.dataset.url}?variant=${variantId}&section_id=${encodeURIComponent(this.originalSectionId)}`
        );
        let text = await response.text();
        if (this.originalSectionId !== this.sectionId) text = text.split(this.originalSectionId).join(this.sectionId);
        updateQuantityRules(new DOMParser().parseFromString(text, 'text/html'), this.sectionId);
        this.setQuantityBoundaries();
      } catch (error) {
        console.error(error);
      } finally {
        const freshSpinner = container.querySelector('.quantity__rules-cart .loading-overlay');
        if (freshSpinner) freshSpinner.classList.add('hidden');
      }
    }
  }

  /* ======================================================================
   * <variant-selects> / <variant-radios>
   * ==================================================================== */

  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    get variants() {
      return readVariants(this);
    }

    get picker() {
      return OptionPicker.fromContainers(this.querySelectorAll(':scope > .product-form__input'));
    }

    onChange(event) {
      if (this.picker.indexOf(event.target) === -1) return;
      this.updateFromSelection();
    }

    updateFromSelection(source = this) {
      this.currentVariant = resolveSelection(this.picker, this.variants, {
        skipNonExistent: this.dataset.skipNonExistent === 'true',
        skipUnavailable: this.dataset.skipUnavailable === 'true',
      });
      applyVariantChange(buildContext(this), this.currentVariant, source);
    }

    /** Used by secondary pickers (sticky bar) to drive the main picker. */
    selectVariant(variant, source = this) {
      if (!variant) return;
      this.picker.setValues(variant.options);
      this.updateFromSelection(source);
    }
  }

  class VariantRadios extends VariantSelects {}

  /* ======================================================================
   * Sticky bar pickers: <secondary-variant-select> / <secondary-variant-select-separate>
   * ==================================================================== */

  class SecondaryVariantSelect extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
      document.addEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    get variants() {
      return readVariants(this);
    }

    get select() {
      return this.querySelector('select');
    }

    onChange(event) {
      if (event.target !== this.select) return;
      const value = this.select.value;
      this.dispatchVariant(this.variants.find((variant) => variant.options.join(',') === value) || null);
    }

    dispatchVariant(variant) {
      const mainPicker = findMainPicker(this.dataset.section);
      if (mainPicker && variant) mainPicker.selectVariant(variant, this);
      else applyVariantChange(buildContext(this), variant, this);
    }

    onVariantChange(event) {
      const { sectionId, variant, source } = event.detail;
      if (sectionId !== this.dataset.section || source === this || !variant) return;
      this.syncTo(variant);
    }

    syncTo(variant) {
      const select = this.select;
      if (!select) return;
      const key = variant.options.join(',');
      const option = Array.from(select.options).find((item) => item.value === key);
      if (option) select.value = option.value;
    }
  }

  class SecondaryVariantSelectSeparate extends SecondaryVariantSelect {
    get picker() {
      return OptionPicker.fromSelects(this.querySelectorAll('select'));
    }

    onChange(event) {
      const picker = this.picker;
      if (picker.indexOf(event.target) === -1) return;
      const variant = resolveSelection(picker, this.variants, { skipNonExistent: true });
      this.dispatchVariant(variant);
    }

    syncTo(variant) {
      const picker = this.picker;
      picker.setValues(variant.options);
      resolveSelection(picker, this.variants);
    }
  }

  /* ======================================================================
   * <card-variant-selects> (product cards)
   * ==================================================================== */

  class CardVariantSelects extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    onChange(event) {
      const picker = OptionPicker.fromContainers(this.querySelectorAll(':scope > .product-form__input'));
      const changedIndex = picker.indexOf(event.target);
      if (changedIndex === -1) return;

      const variants = readVariants(this);
      const values = picker.getValues();
      const variant = findClosestVariant(variants, values, changedIndex);
      if (variant) picker.setValues(variant.options);
      resolveSelection(picker, variants);
      this.applyVariant(variant);
    }

    applyVariant(variant) {
      const formId = this.dataset.section;
      const form = byId(formId);
      const card = this.closest('.card-wrapper') || this.parentElement;
      if (!variant) return;

      const input = form && form.querySelector('[name="id"]');
      if (input) input.value = variant.id;

      setImage(byId(`${formId}-image`), variant.featured_image, 533);
      if (card) updatePriceBlock(card.querySelector('.price'), variant);

      const button = byId(`${formId}-submit`);
      if (button && button.closest('product-form')) {
        setButtonDisabled(button, !variant.available);
        const label = button.querySelector('.main-atc__label__text');
        if (label) label.textContent = variant.available ? strings().addToCart : strings().soldOut;
        const soldOut = button.querySelector('.sold-out-message');
        if (soldOut) soldOut.classList.add('hidden');
        if (label) label.classList.remove('hidden');
      }
    }
  }

  /** Client side update of a snippets/price.liquid block. */
  function updatePriceBlock(priceElement, variant) {
    if (!priceElement || !variant) return;
    const onSale = variant.compare_at_price > variant.price;
    const price = money(variant.price);

    priceElement.classList.toggle('price--on-sale', onSale);
    priceElement.classList.toggle('price--sold-out', !variant.available);
    priceElement.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach((el) => {
      el.textContent = price;
    });
    const compare = priceElement.querySelector('.price__compare-price .price-item--regular');
    if (compare) compare.textContent = onSale ? money(variant.compare_at_price) : '';
  }

  /* ======================================================================
   * <sticky-atc>
   * ==================================================================== */

  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.onAtcClick = this.onAtcClick.bind(this);
      this.onScrollButtonClick = this.onScrollButtonClick.bind(this);
      this.updateOffset = this.updateOffset.bind(this);
      this.isShown = false;
    }

    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.atcButton = this.querySelector('[id^="SectionAtcBtn-"]');
      this.scrollButton = this.querySelector('.sticky-atc__scroll-btn');

      if (this.atcButton) this.atcButton.addEventListener('click', this.onAtcClick);
      if (this.scrollButton) this.scrollButton.addEventListener('click', this.onScrollButtonClick);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateOffset);
        this.resizeObserver.observe(this);
      }

      if (this.dataset.afterScroll === 'true') this.observeTrigger();
      else this.toggle(true);
    }

    disconnectedCallback() {
      if (this.atcButton) this.atcButton.removeEventListener('click', this.onAtcClick);
      if (this.scrollButton) this.scrollButton.removeEventListener('click', this.onScrollButtonClick);
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.isShown = false;
      this.updateOffset();
    }

    observeTrigger() {
      const trigger = byId(`ProductSubmitButton-${this.sectionId}`) || getProductInfo(this.sectionId);
      if (!trigger || !('IntersectionObserver' in window)) {
        this.toggle(true);
        return;
      }
      this.toggle(false);
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.intersectionObserver.observe(trigger);
    }

    toggle(visible) {
      this.isShown = visible;
      if (this.dataset.afterScroll === 'true') {
        this.style.transform = visible ? 'translateY(0)' : '';
        this.toggleAttribute('inert', !visible);
        this.setAttribute('aria-hidden', String(!visible));
      }
      this.updateOffset();
    }

    // Floating buttons (scroll to top, music player) sit above the bar.
    updateOffset() {
      const offset = this.isShown && this.isConnected ? `${this.offsetHeight}px` : '0px';
      document.querySelectorAll('.music-player, .scroll-to-top-btn').forEach((element) => {
        element.style.setProperty('--sticky-atc-offset', offset);
      });
    }

    onAtcClick(event) {
      const form = resolveOwnerForm(this.atcButton) || byId(`ProductForm-${this.sectionId}`);
      if (form && typeof form.handleSubmit === 'function') form.handleSubmit(event);
    }

    onScrollButtonClick(event) {
      event.preventDefault();
      let destination = this.dataset.scrollDestination || '';
      if (destination.endsWith('-id')) destination = `${destination.slice(0, -2)}${this.sectionId}`;

      let target = null;
      try {
        target = destination ? document.querySelector(destination) : null;
      } catch (error) {
        target = null;
      }
      if (!target) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const header = document.querySelector('sticky-header');
      const headerOffset = header ? header.offsetHeight : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 10;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }
  }

  /* ======================================================================
   * <quantity-breaks>
   * ==================================================================== */

  const PRICE_PLACEHOLDERS = /\[(quantity|price|compare_price|amount_saved|amount_saved_rounded|price_each|compare_price_each)\]/g;

  function renderPriceText(template, values, format) {
    return template.replace(PRICE_PLACEHOLDERS, (_, key) => {
      if (key === 'quantity') return String(values.quantity);
      return `<span>${moneyWithoutTrailingZeros(values[key], format)}</span>`;
    });
  }

  class QuantityBreaks extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
      document.addEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener(VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    get variants() {
      return readVariants(this);
    }

    get selectorItems() {
      return Array.from(this.querySelectorAll('.quantity-break__selector-item'));
    }

    onChange(event) {
      const item = event.target.closest('.quantity-break__selector-item');
      if (item && event.target.matches('select')) this.resolveItem(item);
    }

    resolveItem(item) {
      const picker = OptionPicker.fromSelects(item.querySelectorAll('select'));
      const variant = resolveSelection(picker, this.variants, {
        skipNonExistent: this.dataset.skipNonExistent === 'true',
        skipUnavailable: this.dataset.skipUnavailable === 'true',
      });
      item.dataset.selectedId = variant ? variant.id : '';
      item.dataset.available = String(Boolean(variant && variant.available));
      return variant;
    }

    onVariantChange(event) {
      const { sectionId, variant } = event.detail;
      if (sectionId !== this.dataset.section || !variant) return;

      // The main picker acts as "set all": align every per-unit selector with it.
      this.selectorItems.forEach((item) => {
        OptionPicker.fromSelects(item.querySelectorAll('select')).setValues(variant.options);
        this.resolveItem(item);
      });

      if (this.dataset.updatePrices === 'true') this.updatePrices(variant);
    }

    updatePrices(variant) {
      const format = moneyFormatFor(this);

      this.querySelectorAll('label.quantity-break[data-quantity]').forEach((label) => {
        const quantity = parseInt(label.dataset.quantity, 10) || 1;
        const percentageLeft = toNumber(label.dataset.percentageLeft, 1);
        const fixedDiscount = toNumber(label.dataset.fixedDiscount, 0);
        const useCompare = (label.dataset.comparePrice || 'compare_price') === 'compare_price';

        const price = Math.round(variant.price * quantity * percentageLeft - fixedDiscount);
        const unitCompare =
          useCompare && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
        const comparePrice = unitCompare * quantity;
        const amountSaved = comparePrice - price;
        const values = {
          quantity,
          price,
          compare_price: comparePrice,
          amount_saved: amountSaved,
          amount_saved_rounded: Math.round(amountSaved / 100) * 100,
          price_each: Math.round(price / quantity),
          compare_price_each: Math.round(comparePrice / quantity),
        };

        label.querySelectorAll('.variant-price-update[data-text]').forEach((element) => {
          element.innerHTML = renderPriceText(element.dataset.text, values, format);
          if (element.classList.contains('quantity-break__compare-price')) {
            element.classList.toggle('hidden', comparePrice <= price);
          }
        });
      });
    }

    /**
     * When the selected break has per-unit variant selectors, returns the grouped
     * line items for them; otherwise null (the form's id × quantity is used).
     */
    getSelectedVariantItems() {
      const checked = this.querySelector('input[name="quantity"]:checked');
      if (!checked) return null;
      const label = this.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
      const items = label ? Array.from(label.querySelectorAll('.quantity-break__selector-item')) : [];
      if (!items.length) return null;

      const counts = new Map();
      items.forEach((item) => {
        const id = item.dataset.selectedId;
        if (!id) throw new Error(strings().unavailable || '');
        counts.set(id, (counts.get(id) || 0) + 1);
      });
      return Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
    }
  }

  /* ======================================================================
   * <quantity-gifts>
   * ==================================================================== */

  class QuantityGifts extends HTMLElement {
    constructor() {
      super();
      this.update = this.update.bind(this);
    }

    connectedCallback() {
      document.addEventListener('change', this.update);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.update);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.update);
      if (this.unsubscribe) this.unsubscribe();
    }

    get gifts() {
      return Array.from(this.querySelectorAll('.quantity-gift'));
    }

    update() {
      const quantity = getSectionQuantity(this.dataset.section);
      this.gifts.forEach((gift) => {
        const required = parseInt(gift.dataset.quantity, 10);
        const unlocked = Number.isFinite(required) && quantity >= required;
        if (gift.dataset.unlocked === String(unlocked)) return;
        gift.dataset.unlocked = String(unlocked);
        gift.classList.toggle('quantity-gift--unlocked', unlocked);
      });
    }

    getItems() {
      this.update();
      return this.gifts
        .filter((gift) => gift.dataset.unlocked === 'true' && gift.dataset.product)
        .map((gift) => ({ id: Number(gift.dataset.product), quantity: 1 }));
    }
  }

  /* ======================================================================
   * Bundles: <bundle-deals>, <main-bundle-offer>
   * ==================================================================== */

  function updateBundleTotals(root) {
    const format = moneyFormatFor(root);
    const percentageLeft = toNumber(root.dataset.percentageLeft, 1);
    const fixedDiscount = toNumber(root.dataset.fixedDiscount, 0);
    let price = 0;
    let comparePrice = 0;
    let count = 0;

    root.querySelectorAll('.bundle-deals__checkbox-js').forEach((checkbox) => {
      if (!checkbox.checked) return;
      count += 1;
      price += toNumber(checkbox.dataset.price, 0);
      comparePrice += toNumber(checkbox.dataset.comparePrice, 0);
    });

    const total = count ? Math.max(Math.round(price * percentageLeft - fixedDiscount), 0) : 0;
    root.querySelectorAll('.bundle-deals__total-price-js').forEach((element) => {
      element.textContent = money(total, format);
    });
    root.querySelectorAll('.bundle-deals__total-compare-price-js').forEach((element) => {
      element.textContent = comparePrice > total ? money(comparePrice, format) : '';
    });
  }

  /** Writes price / compare price for one bundle or upsell item. */
  function renderItemPrices(container, price, comparePrice, format) {
    container.querySelectorAll('.bundle-deals__price-js, .regular-price').forEach((element) => {
      if (element.closest('.bundle-offer__footer')) return;
      element.textContent = money(price, format);
    });
    container.querySelectorAll('.bundle-deals__compare-price-js, .compare-price').forEach((element) => {
      if (element.closest('.bundle-offer__footer')) return;
      const show = comparePrice > price;
      element.textContent = show ? money(comparePrice, format) : '';
      element.classList.toggle('hidden', !show);
    });
  }

  class BundleDeals extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    get checkboxes() {
      return Array.from(this.querySelectorAll('.bundle-deals__checkbox-js'));
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('.bundle-deals__checkbox-js')) {
        this.onToggle(target);
      } else if (target.matches('select') && target.closest('.bundle-deals__variant-selects-js')) {
        this.onVariantSelect(target.closest('.bundle-deals__variant-selects-js'));
      }
    }

    onToggle(checkbox) {
      checkbox.dataset.checked = String(checkbox.checked);
      const product = checkbox.closest('.bundle-deals__product-js');
      if (product) product.classList.toggle('bundle-deals__product--deselected', !checkbox.checked);
      const mediaItem = this.querySelectorAll('.bundle-deals__media-item')[Number(checkbox.dataset.index)];
      if (mediaItem) mediaItem.classList.toggle('bundle-deals__media-item--disabled', !checkbox.checked);
      this.updateTotals();
    }

    /** "Enable dynamic price changes" off keeps the Liquid-rendered total (currency converters). */
    updateTotals() {
      if (this.dataset.updatePrices === 'true') updateBundleTotals(this);
    }

    onVariantSelect(container) {
      const variants = readVariants(container);
      const variant = resolveSelection(OptionPicker.fromSelects(container.querySelectorAll('select')), variants, {
        skipNonExistent: this.dataset.skipNonExistent === 'true',
        skipUnavailable: this.dataset.skipUnavailable === 'true',
      });
      const index = container.dataset.index;
      const product = container.closest('.bundle-deals__product-js') || this;
      const checkbox =
        product.querySelector('.bundle-deals__checkbox-js') ||
        this.querySelector(`.bundle-deals__checkbox-js[data-index="${CSS.escape(index)}"]`);
      if (!checkbox) return;

      checkbox.dataset.available = String(Boolean(variant && variant.available));
      if (!variant) return;
      checkbox.dataset.id = variant.id;

      // Per-item discount lives on the picker (bundle-deals section) or the item wrapper.
      const discountSource = container.dataset.percentageLeft ? container : checkbox.closest('[data-percentage-left]');
      const percentageLeft = toNumber(discountSource && discountSource.dataset.percentageLeft, 1);
      const fixedDiscount = toNumber(discountSource && discountSource.dataset.fixedDiscount, 0);

      if (this.dataset.updatePrices === 'true') {
        const price = Math.max(Math.round(variant.price * percentageLeft - fixedDiscount), 0);
        const comparePrice = Math.max(variant.compare_at_price || 0, variant.price);
        checkbox.dataset.price = price;
        checkbox.dataset.comparePrice = comparePrice;
        renderItemPrices(product === this ? container : product, price, comparePrice, moneyFormatFor(this));
      }

      // Images live inside the item (bundle offer block) or in a separate media column (section).
      const mediaItem = this.querySelectorAll('.bundle-deals__media-item')[Number(index)];
      const image =
        (product !== this && product.querySelector('.bundle-deals__media-item-img-js')) ||
        (mediaItem && mediaItem.querySelector('.bundle-deals__media-item-img-js'));
      setImage(image, variant.featured_image, 800);
      this.updateTotals();
    }

    getItems() {
      return this.checkboxes
        .filter((checkbox) => checkbox.checked && checkbox.dataset.id && checkbox.dataset.available !== 'false')
        .map((checkbox) => ({ id: Number(checkbox.dataset.id), quantity: 1 }));
    }
  }

  class MainBundleOffer extends HTMLElement {
    constructor() {
      super();
      this.update = () => updateBundleTotals(this);
    }

    connectedCallback() {
      this.addEventListener(UPSELL_CHANGE_EVENT, this.update);
      this.addEventListener('change', this.update);
    }

    disconnectedCallback() {
      this.removeEventListener(UPSELL_CHANGE_EVENT, this.update);
      this.removeEventListener('change', this.update);
    }
  }

  /* ======================================================================
   * <product-info-upsell>
   * ==================================================================== */

  class ProductInfoUpsell extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
    }

    get isSelected() {
      return this.dataset.selected === 'true';
    }

    onClick(event) {
      if (this.dataset.toggle !== 'true') return;
      const toggle = event.target.closest('.upsell-toggle-btn');
      if (!toggle || !this.contains(toggle)) return;
      // A clickable container must not toggle when using the picker, links or gallery.
      if (event.target.closest('select, a, upsell-gallery, .upsell__variant-picker, .bundle-deals__variant-selects-js')) {
        return;
      }
      event.preventDefault();
      this.toggleSelected();
    }

    toggleSelected(force) {
      const selected = typeof force === 'boolean' ? force : !this.isSelected;
      this.dataset.selected = String(selected);
      this.dispatchEvent(new CustomEvent(UPSELL_CHANGE_EVENT, { bubbles: true, detail: { selected } }));
    }

    onChange(event) {
      const container = event.target.closest('.upsell__variant-picker, .bundle-deals__variant-selects-js');
      if (!container || !event.target.matches('select')) return;

      const variant = resolveSelection(OptionPicker.fromSelects(container.querySelectorAll('select')), readVariants(container), {
        skipNonExistent: this.dataset.skipNonExistent === 'true',
        skipUnavailable: this.dataset.skipUnavailable === 'true',
      });
      this.applyVariant(variant);
    }

    applyVariant(variant) {
      const available = Boolean(variant && variant.available);
      this.dataset.available = String(available);

      const addButton = this.querySelector('.upsell__add-btn');
      if (addButton) {
        const label = addButton.querySelector('span');
        if (label && addButton.dataset.label === undefined && !addButton.hasAttribute('disabled')) {
          addButton.dataset.label = label.textContent.trim();
        }
        setButtonDisabled(addButton, !available);
        if (label) {
          label.textContent = available
            ? addButton.dataset.label || strings().addToCart
            : variant
              ? strings().soldOut
              : strings().unavailable;
        }
      }

      if (variant) {
        this.dataset.id = variant.id;
        const input = this.querySelector('product-form [name="id"]');
        if (input) input.value = variant.id;
        const checkbox = this.querySelector('.bundle-deals__checkbox-js');
        if (checkbox) checkbox.dataset.id = variant.id;

        if (this.dataset.updatePrices === 'true') {
          const percentageLeft = toNumber(this.dataset.percentageLeft, 1);
          const fixedDiscount = toNumber(this.dataset.fixedDiscount, 0);
          const price = Math.max(Math.round(variant.price * percentageLeft - fixedDiscount), 0);
          const comparePrice = Math.max(variant.compare_at_price || 0, variant.price);
          this.dataset.price = price;
          this.dataset.comparePrice = comparePrice;
          if (checkbox) {
            checkbox.dataset.price = price;
            checkbox.dataset.comparePrice = comparePrice;
          }
          renderItemPrices(this, price, comparePrice, moneyFormatFor(this));
        }

        const image = this.querySelector('.upsell__image__img, .bundle-deals__media-item-img-js, .upsell__image img');
        setImage(image, variant.featured_image, 500);
      }

      this.dispatchEvent(new CustomEvent(UPSELL_CHANGE_EVENT, { bubbles: true, detail: { variant } }));
    }
  }

  /* ======================================================================
   * <custom-product-field>
   * ==================================================================== */

  class CustomProductField extends HTMLElement {
    constructor() {
      super();
      this.onInput = this.onInput.bind(this);
    }

    connectedCallback() {
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onInput);
      refreshAtcState(this.dataset.section);
    }

    disconnectedCallback() {
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onInput);
      refreshAtcState(this.dataset.section);
    }

    get control() {
      return this.querySelector('input:not([type="radio"]), textarea, select');
    }

    get isRequired() {
      return this.dataset.required === 'true';
    }

    getValue() {
      if (this.dataset.type === 'pills') {
        const checked = this.querySelector('input[type="radio"]:checked');
        return checked ? checked.value.trim() : '';
      }
      const control = this.control;
      return control ? control.value.trim() : '';
    }

    isValid() {
      const control = this.control;
      if (control && control.value !== '' && typeof control.checkValidity === 'function' && !control.checkValidity()) {
        return false;
      }
      return !this.isRequired || this.getValue() !== '';
    }

    showError(show) {
      const control = this.control;
      if (control) control.classList.toggle('input--error', show);
    }

    onInput() {
      this.showError(!this.isValid());
      refreshAtcState(this.dataset.section);
    }
  }

  /* ======================================================================
   * Registration
   * ==================================================================== */

  const elements = {
    'product-form': ProductForm,
    'product-info': ProductInfo,
    'variant-selects': VariantSelects,
    'variant-radios': VariantRadios,
    'secondary-variant-select': SecondaryVariantSelect,
    'secondary-variant-select-separate': SecondaryVariantSelectSeparate,
    'card-variant-selects': CardVariantSelects,
    'sticky-atc': StickyAtc,
    'quantity-breaks': QuantityBreaks,
    'quantity-gifts': QuantityGifts,
    'bundle-deals': BundleDeals,
    'main-bundle-offer': MainBundleOffer,
    'product-info-upsell': ProductInfoUpsell,
    'custom-product-field': CustomProductField,
  };

  Object.entries(elements).forEach(([name, constructor]) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  });
})();
